import { TimetableReportType } from '@bp/planung-graphql-types';
import { Card, EmptyState, Grid, GridColumn, GridRow, NoTasksIcon } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useUserConfigContext } from '../../hooks/useUserConfigContext';
import {
  SortDirection,
  use_TeachingBlocksQuery,
  use_TimetableBlocksQuery,
  useTimetableReportsQuery,
} from '../../types/planung-graphql-client-defs';
import {
  TimetableReportTypesTable,
  TimetableReportTypesTableType,
} from '../Timetable/tables/TimetableReportTypesTable';
import { TimetablePrintConfigurationModal } from '../TimetablePrintConfiguration/modals/TimetablePrintConfigurationModal';
import { TimetableReportsTable, TimetableReportsTableType } from './TimetableReportsTable';
import { ReportTopic } from './types';

type TimetableReportsProps = {
  topic: ReportTopic;
};

export const TimetableReports = observer(({ topic }: TimetableReportsProps) => {
  const { t } = useTranslation();
  const permissions = usePermissionChecker();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
  const timetableBlocksContext = useMemorizedCacheTag('TIMETABLE_BLOCK');
  const teachingBlocksContext = useMemorizedCacheTag('TEACHINGBLOCK');

  const tableData: TimetableReportTypesTableType[] = useMemo(() => {
    switch (topic) {
      case 'classes':
        return [
          { type: TimetableReportType.SingleClass, name: t('pdf.singlePlans') },
          { type: TimetableReportType.AllClasses, name: t('pdf.overallPlans') },
          {
            type: TimetableReportType.TeachingBlocksClass,
            name: t('pdf.epochPlans'),
          },
          {
            type: TimetableReportType.AttendanceSchedule,
            name: t('pdf.attendanceSchedule'),
          },
        ];
      case 'rooms':
        return [
          {
            type: TimetableReportType.SingleRoom,
            name: t('pdf.singlePlans'),
          },
          { type: TimetableReportType.DoorSigns, name: t('pdf.doorSigns') },
          { type: TimetableReportType.AllRooms, name: t('pdf.overallPlans') },
          {
            type: TimetableReportType.TeachingBlocksRoom,
            name: `${t('pdf.epochPlans')}`,
          },
        ];
      case 'teachers':
        return [
          {
            type: TimetableReportType.SingleTeacher,
            name: t('pdf.singlePlans'),
          },
          {
            type: TimetableReportType.AllTeachers,
            name: t('pdf.overallPlans'),
          },
          {
            type: TimetableReportType.TeachingBlocksTeacher,
            name: t('pdf.epochPlans'),
          },
        ];
      case 'other':
        return [
          {
            type: TimetableReportType.SchoolTrips,
            name: t('pdf.classTrips'),
          },
        ];
      default:
        return [];
    }
  }, [topic, t]);

  const [headline, setHeadline] = useState<string>(tableData[0].name);
  const [subType, setSubType] = useState<TimetableReportType | null>(tableData[0].type);
  const [loading, setLoading] = useState<boolean>(false);
  const [showConfigModal, setShowConfigModal] = useState<boolean>(false);

  const [{ data: teachingBlocksData }] = use_TeachingBlocksQuery({
    variables: {
      where: {
        schoolYear: {
          uuid: currentSchoolYear?.uuid,
        },
        draft: false,
        versions_SOME: {
          active: true,
        },
      },
    },
    context: teachingBlocksContext,
  });

  const [{ data: timetableBlocksData }] = use_TimetableBlocksQuery({
    variables: {
      where: {
        schoolYear: {
          uuid: currentSchoolYear?.uuid,
        },
        timetables_SOME: {
          versions_SINGLE: {
            active: true,
          },
        },
      },
    },
    context: timetableBlocksContext,
  });

  const [{ data: timetableReportsQueryData }, refetchReports] = useTimetableReportsQuery({
    variables: {
      where:
        subType === TimetableReportType.SchoolTrips
          ? {
              context: {
                SchoolYear: {
                  uuid: currentSchoolYear?.uuid,
                },
              },
            }
          : {
              OR: [
                {
                  context: {
                    TeachingBlock: {
                      uuid_IN: teachingBlocksData?.teachingBlocks.map((tb) => tb.uuid),
                    },
                  },
                },
                {
                  context: {
                    TimetableBlock: {
                      uuid_IN: timetableBlocksData?.timetableBlocks.map((tb) => tb.uuid),
                    },
                  },
                },
              ],
            },
      options: { sort: [{ jobCreated: SortDirection.Desc }] },
    },
    pause:
      subType === TimetableReportType.SchoolTrips
        ? !currentSchoolYear?.uuid
        : teachingBlocksData?.teachingBlocks.length === 0 && timetableBlocksData?.timetableBlocks.length === 0,
  });

  const isSingle =
    subType === TimetableReportType.SingleClass ||
    subType === TimetableReportType.SingleRoom ||
    subType === TimetableReportType.SingleSubject ||
    subType === TimetableReportType.SingleTeacher;

  const timetableReportsData: TimetableReportsTableType[] =
    timetableReportsQueryData?.timetableReports
      .filter((report) => report.type === subType)
      .map((report) => {
        return {
          uuid: report.uuid,
          name: report.title,
          contextUuid: report.context.uuid,
          contextName: report.context.name,
          type: isSingle ? (report.fileEntries.length > 1 ? t('pdf.singlePdf') : t('pdf.collectivePdf')) : '',
          timestamp: dayjs(report.jobFinished),
        };
      })
      .sort((a, b) => {
        return a.timestamp.isAfter(b.timestamp) ? -1 : 1;
      }) ?? [];

  function handleSelect(headline: string, subType: TimetableReportType | null) {
    setHeadline(headline);
    setSubType(subType);
  }

  return (
    <>
      <Grid>
        <GridRow mobileGap={'32px'}>
          <GridColumn width={6}>
            <Card header={{ headline: t('timetable.title', { count: 2 }) }} contentPadding={'none'}>
              <TimetableReportTypesTable data={tableData} onSelect={handleSelect} />
            </Card>
          </GridColumn>
          <GridColumn width={6}>
            <Card
              header={{
                headline,
                actions: [
                  {
                    text: t('timetable.configuration.configure'),
                    callback: () => setShowConfigModal(true),
                    disabled: !permissions?.canWriteTimetable() || loading,
                  },
                ],
              }}
              contentPadding='none'
            >
              {subType ? (
                subType === TimetableReportType.SchoolTrips ? (
                  <div key={currentSchoolYear?.uuid}>
                    <div className='px-6 mb-4'>{currentSchoolYear?.name}</div>
                    <TimetableReportsTable
                      contextUuid={currentSchoolYear?.uuid ?? ''}
                      tableData={timetableReportsData}
                      refetchTableData={refetchReports}
                      type='timetable'
                      subType={subType}
                      onLoad={setLoading}
                    />
                  </div>
                ) : subType === TimetableReportType.TeachingBlocksClass ||
                  subType === TimetableReportType.TeachingBlocksTeacher ||
                  subType === TimetableReportType.TeachingBlocksRoom ||
                  subType === TimetableReportType.TeachingBlocksSubject ? (
                  teachingBlocksData?.teachingBlocks.map((teachingBlock) => {
                    return (
                      <div key={teachingBlock.uuid}>
                        <div className='px-6 mb-4'>{teachingBlock.name}</div>
                        <TimetableReportsTable
                          contextUuid={teachingBlock.uuid}
                          tableData={timetableReportsData}
                          refetchTableData={refetchReports}
                          type='teachingblock'
                          subType={subType}
                          onLoad={setLoading}
                        />
                      </div>
                    );
                  })
                ) : (
                  timetableBlocksData?.timetableBlocks
                    .sort((a, b) => {
                      return dayjs(a.start).isAfter(dayjs(b.start)) ? -1 : 1;
                    })
                    .map((timetableBlock) => {
                      return (
                        <div key={timetableBlock.uuid}>
                          <div className='px-6 mb-4'>{timetableBlock.name}</div>
                          <TimetableReportsTable
                            tableData={timetableReportsData}
                            refetchTableData={refetchReports}
                            contextUuid={timetableBlock.uuid}
                            type='timetable'
                            subType={subType}
                            onLoad={setLoading}
                          />
                        </div>
                      );
                    })
                )
              ) : (
                <EmptyState title={t('common.commingSoon')} icon={<NoTasksIcon />} padding='l' />
              )}
            </Card>
          </GridColumn>
        </GridRow>
      </Grid>

      {subType && (
        <TimetablePrintConfigurationModal
          isOpen={showConfigModal}
          subType={subType}
          onClose={() => setShowConfigModal(false)}
        />
      )}
    </>
  );
});
